/**
 * Tempo detection block (W2-08 timing; DESIGN.md §4.15 "Detect with candidates and x2 / /2 / x1.5", §5.2.1):
 * a Detect button, the candidates with their strength (a label and a bar, never colour alone) and a Use button,
 * and the octave fixes. Presentational: the Song & Timing panel and the wizard Beat step supply the data and the
 * actions.
 */

import type { TempoCandidate } from '../../core/audio-dsp/types';
import { Button, Icon, ProgressBar, Tooltip } from '../../ui';
import { m } from './timing.messages';
import { DECODE_FAILED, NO_DECODER, NOT_DECODED } from './timingAnalysis';
import type { TimingText } from './timingHooks';
import { strengthLevel } from './timingModel';
import s from './Timing.module.css';

export interface TempoDetectProps {
  readonly text: TimingText;
  /** null: not detected yet. */
  readonly candidates: readonly TempoCandidate[] | null;
  readonly detecting: boolean;
  /** Progress of the running detection (0..1) or null. */
  readonly progress: number | null;
  readonly error: string | null;
  /** The tempo in use (to mark the matching candidate). */
  readonly currentBpm: number | null;
  /** Why detection cannot run now, or null. */
  readonly disabledReason?: string | null;
  onDetect(): void;
  onUse(bpm: number): void;
  onMultiply(factor: number): void;
  /** Hide the octave fixes (when there is no tempo to fix). */
  readonly showFixes?: boolean;
}

/** Text of an analysis error (`SongAnalysis.error`). */
export function analysisErrorText(text: TimingText, error: string): string {
  if (error === NOT_DECODED) return text.t(m.notDecoded);
  if (error === NO_DECODER) return text.t(m.noDecoder);
  if (error === DECODE_FAILED) return text.t(m.decodeFailed);
  return text.t(m.analysisFailed, { error });
}

/** Whether two BPMs are the same tempo for the "In use" mark. */
function sameBpm(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

export function TempoDetect(props: TempoDetectProps) {
  const { text, candidates, detecting, currentBpm } = props;
  const reason = props.disabledReason ?? null;
  return (
    <div className={s.stack}>
      <div className={s.row}>
        <Tooltip content={text.t(m.detectTempoTip)}>
          <Button
            icon="AudioWaveform"
            onClick={() => {
              props.onDetect();
            }}
            disabledReason={detecting ? { key: m.detecting } : reason === null ? null : { key: reason }}
            data-testid="timing-detect-tempo"
          >
            {text.t(candidates === null ? m.detect : m.detectAgain)}
          </Button>
        </Tooltip>
        {detecting && (
          <div className={s.grow}>
            <ProgressBar value={props.progress} label={{ key: m.detecting }} />
          </div>
        )}
      </div>
      {props.error !== null && !detecting && (
        <p className={s.error} role="alert">
          <Icon name="CircleAlert" size={14} />
          {analysisErrorText(text, props.error)}
        </p>
      )}
      {candidates !== null && candidates.length === 0 && !detecting && (
        <p className={s.help}>{text.t(m.noCandidates)}</p>
      )}
      {candidates !== null && candidates.length > 0 && (
        <ul className={s.candidates} aria-label={text.t(m.candidatesLabel)}>
          {candidates.map((c) => {
            const level = strengthLevel(c.strength);
            const current = currentBpm !== null && sameBpm(c.bpm, currentBpm);
            const pct = Math.round(c.strength * 100);
            return (
              <li key={c.bpm} className={s.candidate} data-current={current} data-strength={level}>
                <span className={s.mono}>{text.t(m.candidate, { bpm: c.bpm })}</span>
                <Tooltip content={text.t(m.strengthTip, { pct })}>
                  <span className={s.strength} tabIndex={0}>
                    <span className={s.meter} aria-hidden="true">
                      <span className={s.meterFill} style={{ width: `${String(pct)}%` }} />
                    </span>
                    {text.t(m.strength, { level })}
                  </span>
                </Tooltip>
                {current ? (
                  <span className={s.strength}>
                    <Icon name="Check" size={14} />
                    {text.t(m.inUse)}
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      props.onUse(c.bpm);
                    }}
                    aria-label={text.t(m.useCandidateLabel, { bpm: c.bpm })}
                  >
                    {text.t(m.useCandidate)}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {props.showFixes !== false && currentBpm !== null && (
        <div className={s.stack}>
          <p className={s.help}>{text.t(m.octaveHelp)}</p>
          <div className={s.rowTight} role="group" aria-label={text.t(m.octaveHelp)}>
            <Button
              size="sm"
              onClick={() => {
                props.onMultiply(2);
              }}
              aria-label={text.t(m.times2Label)}
            >
              {text.t(m.times2)}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                props.onMultiply(0.5);
              }}
              aria-label={text.t(m.div2Label)}
            >
              {text.t(m.div2)}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                props.onMultiply(1.5);
              }}
              aria-label={text.t(m.times15Label)}
            >
              {text.t(m.times15)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
